import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SiteContentItem {
  page_key: string;
  section_key: string;
  content: Record<string, any>;
}

const cache: Record<string, SiteContentItem[]> = {};

export const useSiteContent = (pageKey: string) => {
  const [items, setItems] = useState<SiteContentItem[]>(cache[pageKey] || []);
  const [loading, setLoading] = useState(!cache[pageKey]);

  useEffect(() => {
    if (cache[pageKey]) {
      setItems(cache[pageKey]);
      setLoading(false);
      return;
    }

    const fetch = async () => {
      const { data } = await supabase
        .from('site_content')
        .select('page_key, section_key, content')
        .eq('page_key', pageKey)
        .order('sort_order');

      const result = (data as unknown as SiteContentItem[]) || [];
      cache[pageKey] = result;
      setItems(result);
      setLoading(false);
    };

    fetch();
  }, [pageKey]);

  const getText = (sectionKey: string, fallback: string = ''): string => {
    const item = items.find(i => i.section_key === sectionKey);
    return (item?.content as any)?.text || fallback;
  };

  const getJson = <T = any>(sectionKey: string, fallback: T): T => {
    const item = items.find(i => i.section_key === sectionKey);
    if (!item?.content) return fallback;
    return item.content as unknown as T;
  };

  return { items, loading, getText, getJson };
};
